
import { Typography } from "antd";
import { Chart } from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { useEffect, useRef } from "react";
import { ChartFont } from "..";
import { DashboardScenarioModel } from "../../../../models/dashboad/dashboard.model";
import { AMOUNT_UTIL } from "../../../../utils/amount.utils";
import { DATE_UTIL } from "../../../../utils/dateUtils";
import "./dashboard-scenario.scss";

interface Props {
    data: DashboardScenarioModel[];
}


const DashboardScenario = (props: Props) => {

    let chart = useRef<Chart>();
    let width: number;
    let height: number;
    let gradient: any;
    const forceUpdate = React.useReducer(() => ({}), {})[1] as () => void

    const getGradient = (ctx: any, chartArea: any) => {
        const chartWidth = chartArea.right - chartArea.left;
        const chartHeight = chartArea.bottom - chartArea.top;
        if (gradient === null || width !== chartWidth || height !== chartHeight) {
            // Create the gradient because this is either the first render
            // or the size of the chart has changed
            width = chartWidth;
            height = chartHeight;
            gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
            gradient.addColorStop(0, "#FFFFFF10");
            // gradient.addColorStop(0.5, "#00000000");
            gradient.addColorStop(1, "#EF753F");
        }

        return gradient;
    }

    const getName = (name: string) => {
        if (name.length <= 10) {
            return name;
        }

        return name.substr(0, 9) + "...";
    }

    const getChartData = (): any => {

        const labels: string[][] = [];
        const max_expected_loss: number[] = [];
        const net_loss: number[] = [];
        const recovery: number[] = [];
        const roi: number[] = [];
        const mitigation_project: number[] = [];


        props.data.forEach(x => {
            labels.push([getName(x.scenario.name), DATE_UTIL.format(x.scenario.date, "MM/DD/YYYY")]);
            max_expected_loss.push(x.data.max_expected_loss);
            net_loss.push(x.data.net_loss);
            recovery.push(x.data.recovery);
            roi.push(x.data.roi);
            mitigation_project.push(x.data.mitigation_project);
        });


        return {
            labels: labels,
            datasets: [
                {
                    label: 'Max. Expected Loss',
                    data: max_expected_loss,
                    backgroundColor: "#9AC4F8",
                    stack: 'Stack 0',
                    order: 1,
                    yAxisID: 'y',
                    datalabels: {
                        display: false
                    }
                },
                {
                    label: 'Recovery',
                    data: recovery,
                    backgroundColor: "#E1CE7A",
                    stack: 'Stack 1',
                    order: 1,
                    yAxisID: 'y',
                    datalabels: {
                        display: false
                    }
                },
                {
                    label: 'Net Loss',
                    data: net_loss,
                    backgroundColor: "#A4303F",
                    stack: 'Stack 3',
                    order: 1,
                    yAxisID: 'y',
                    datalabels: {
                        display: false
                    }
                },
                {
                    label: 'Mitigation Project',
                    data: mitigation_project,
                    backgroundColor: "#41D3BD",
                    stack: 'Stack 4',
                    order: 1,
                    yAxisID: 'y',
                    datalabels: {
                        display: false
                    }
                },
                {
                    type: 'line',
                    label: 'ROI',
                    data: roi,
                    backgroundColor: function (context: any) {
                        const chart = context.chart;
                        const { ctx, chartArea } = chart;

                        if (!chartArea) {
                            // This case happens on initial chart load
                            return null;
                        }
                        return getGradient(ctx, chartArea);
                    },
                    borderColor: "#EF753F",
                    pointBackgroundColor: "#ffffff",
                    fill: true,
                    borderWidth: 1,
                    order: 0,
                    yAxisID: 'y2',
                    datalabels: {
                        offset: 2,
                        align: function (context: any) {
                            var index = context.dataIndex;
                            var value = context.dataset.data[index];
                            return value < 90 ? 'end' : 'start'
                        },
                        color: "#303030",
                        font: {
                            weight: 'bold',
                            size: 10
                        },
                        formatter: function (value: number) {
                            return AMOUNT_UTIL.converNumberToPercentage(value, 0);
                        }
                    }
                }
            ]
        }
    }

    const lengendClick = (label: string) => {
        if (chart.current) {
            chart.current.data.datasets.forEach(dataset => {
                if (dataset.label === label) {
                    dataset.hidden = !dataset.hidden;
                }
            });

            chart.current.update();
            forceUpdate();
        }

    }


    const inintChart = () => {

        const element: any = document.getElementById("scenarioChart");
        const ctx = element.getContext('2d');
        const chartData = getChartData();

        chart.current = new Chart(ctx, {
            type: 'bar',
            data: chartData,
            plugins: [ChartDataLabels],
            options: {
                scales: {
                    y: {
                        position: 'left',
                        beginAtZero: true,
                        ticks: {
                            // Include a dollar sign in the ticks
                            callback: function (value, index, values) {
                                return AMOUNT_UTIL.convertNumericToFormattedAmount(Number(value), 0);
                            },
                            font: ChartFont,
                            color: "#969696"
                        }
                    },
                    y2: {
                        position: 'right',
                        max: 100,
                        display: false,
                        ticks: {
                            // Include a dollar sign in the ticks
                            callback: function (value, index, values) {
                                return "";
                            },
                        }
                    },
                    x: {
                        ticks: {
                            font: ChartFont,
                            color: "#969696"
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                },
                animation: {

                }
            },
        });

        forceUpdate();
    }

    const updateChart = () => {
        if (chart.current) {
            const chartData = getChartData();
            chart.current.data = chartData;
            chart.current.update();
            forceUpdate();
        }
    }

    useEffect(() => {
        if (props.data && props.data.length) {
            if (chart.current) {
                updateChart();
            } else {
                inintChart();
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data])

    return (
        <div className="chart-wrapper">
            <p className="title">
                Management of Forward Looking Scenarios
            </p>
            <canvas height={200} id="scenarioChart"></canvas>
            <div className="chart-legend">
                {
                    chart.current?.data.datasets.map((item: any) => {

                        return (
                            <div key={item.label} className="legend-item" onClick={() => lengendClick(item.label)}>

                                {
                                    item.label?.toUpperCase() === 'ROI' ?
                                        <div className="line" style={{ backgroundColor: item.borderColor?.toString() }}>
                                            <span className="point"
                                                style={{
                                                    backgroundColor: item.pointBackgroundColor?.toString(),
                                                    borderColor: item.borderColor?.toString()
                                                }}
                                            >

                                            </span>
                                        </div> :
                                        <div className="line" style={{ backgroundColor: item.backgroundColor?.toString() }}>
                                        </div>
                                }


                                <Typography.Text delete={item.hidden}>
                                    {item.label}
                                </Typography.Text>

                            </div>
                        )
                    })
                }
            </div>
        </div>
    )
}


export default React.memo(DashboardScenario)
